// Formatadores pt-BR canônicos do app. Antes duplicados inline em 15+ arquivos
// (páginas, drawers, cards do rail) — consolidados aqui preservando as variantes
// de comportamento que são intencionais por tela:
//   fmtMoeda        → BRL SEM centavos (painéis de gestão; centavo é ruído)
//   fmtMoedaPrecisa → BRL COM centavos (preços unitários: cotação, NFe)
//   fmtMoedaAbrev   → k/M abreviado (cards de resumo financeiro)
// null/undefined → '—' (o JSX consumidor não precisa de check).
// `utils/format.ts` (money null→'R$ 0,00', máscaras CNPJ/fone) tem contrato
// diferente e segue separado — unificação é follow-up.

export const fmtMoeda = (v?: number | null): string =>
  v == null
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

export const fmtMoedaPrecisa = (v?: number | null): string =>
  v == null
    ? '—'
    : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const fmtMoedaAbrev = (v: number): string => {
  if (Math.abs(v) >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `R$ ${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const fmtNumero = (v?: number | null, digits = 0): string =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: digits });

export const fmtPercent = (v?: number | null): string =>
  v == null ? '—' : `${Number(v).toFixed(1)}%`;

export const fmtData = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

export const fmtDataHora = (s?: string | null, comSegundos = false): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(comSegundos ? { second: '2-digit' as const } : {}),
  });
};

/** Tempo relativo curto pt-BR ("agora", "5 min", "2 h", "3 d") — feed de notificações. */
export const fmtTempoRelativo = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const seg = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seg < 45) return 'agora';
  if (seg < 3600) return `${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `${Math.floor(seg / 3600)} h`;
  if (seg < 7 * 86400) return `${Math.floor(seg / 86400)} d`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/** Peso em kg → kg/t/kt conforme a grandeza (totalizadores da Análise). */
export const fmtPeso = (kg: number): string => {
  if (!Number.isFinite(kg) || kg === 0) return '0 kg';
  if (kg >= 1_000_000) return `${(kg / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kt`;
  if (kg >= 1_000) return `${(kg / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} t`;
  return `${kg.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg`;
};

/** Tamanho de arquivo B/KB/MB (anexos de cotação). */
export const fmtBytes = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

/** ANOMES "MMYYYY"… na prática "YYYYMM" de 6 chars → "MM/YY" (séries mensais). */
export const fmtAnoMes = (s: string): string =>
  s.length === 6 ? `${s.slice(4, 6)}/${s.slice(2, 4)}` : s;
